import React, { useEffect, useMemo, useState } from 'react';
import swal from 'sweetalert';
import LoginHeaderOperational from './LoginHeaderOperational';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import { API_URL } from '../../constant/API_Settings';
import { fetchReportJson, reportNumberFormat, downloadReportPdf, formatReportDate, formatReportDateRange } from '../../utils/reportFormat';
import logo from '../../img/logo.svg';
import {
  OtherReportActionButtons,
  OtherReportFilterCard,
  ReportDateRangeFields,
  OtherReportLoader,
  OtherReportPageHeader,
  OtherReportResultsZone,
  OtherReportShell,
  OtherReportToolbar,
} from './other-reports/OtherReportShared';
import { validateOperationalDateRange } from '../../utils/operationalListFilters';

const LooseOilBarrelSalesReport = (props) => {
  const [meta, setMeta] = useState({});
  const [inputValues, setInputValues] = useState({});
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetchReportJson(`${API_URL}/loose_oil_barrel_sales_report/meta`)
      .then(setMeta)
      .catch((error) => swal('Loose Oil & Barrel Sales', error.message, 'error'));
  }, []);

  const buildPayload = () => ({
    from_date: inputValues.start_date,
    to_date: inputValues.end_date,
  });

  const validateFilters = () => {
    if (!inputValues.start_date || !inputValues.end_date) {
      swal('Please select start and end dates', '', 'warning');
      return false;
    }
    if (!validateOperationalDateRange(inputValues.start_date, inputValues.end_date)) {
      return false;
    }
    return true;
  };

  const loadReport = async () => {
    if (!validateFilters()) {
      return;
    }

    setLoading(true);
    setReport(null);

    try {
      setReport(await fetchReportJson(`${API_URL}/loose_oil_barrel_sales_report`, buildPayload()));
    } catch (error) {
      swal('Loose Oil & Barrel Sales', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!validateFilters()) {
      return;
    }

    setPdfLoading(true);
    try {
      await downloadReportPdf(
        `${API_URL}/loose_oil_barrel_sales_report/pdf`,
        buildPayload(),
        'loose_oil_barrel_sales_report.pdf',
      );
    } catch (error) {
      swal('Loose Oil & Barrel Sales', error.message, 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const totalsRow = useMemo(() => {
    if (report?.totals_row) return report.totals_row;
    const rows = report?.rows || [];
    return {
      label: 'Total',
      quantity: rows.reduce((sum, row) => sum + Number(row.quantity || 0), 0),
      selling_price: rows.reduce((sum, row) => sum + Number(row.selling_price || 0), 0),
      sales_value: rows.reduce((sum, row) => sum + Number(row.sales_value || 0), 0),
    };
  }, [report]);

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={14} operation={3} op={3} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title=" " />
          <OtherReportShell>
            <OtherReportPageHeader title="Loose Oil & Barrel Sales Statement" badge="Reports" />
            <OtherReportFilterCard>
              <OtherReportToolbar
                actionsWide
                dates={(
                  <>
                    <ReportDateRangeFields
                      startDate={inputValues.start_date}
                      endDate={inputValues.end_date}
                      onChange={({ start_date, end_date }) => setInputValues({
                        ...inputValues,
                        start_date,
                        end_date,
                      })}
                    />
                  </>
                )}
                actions={(
                  <OtherReportActionButtons
                    loading={loading}
                    pdfLoading={pdfLoading}
                    onSubmit={loadReport}
                    onDownloadPdf={downloadPdf}
                  />
                )}
              />
            </OtherReportFilterCard>
            <OtherReportResultsZone>
              {loading && <OtherReportLoader message="Generating loose oil and barrel sales report" />}
              {report && !loading && (
                <div className="form-wrap balace-sheetwrap">
                  <div className="row mb-3">
                    <div className="col-md">
                      <h2 className="table-title mb-1">{report.pump_name || meta.pump_name}</h2>
                      <h4 className="table-title mb-1">{report.type}</h4>
                      <h5>{formatReportDateRange(report.start_date, report.end_date)}</h5>
                      <img src={logo} className="img-fluid float-end" width="100" alt="logo" />
                    </div>
                  </div>
                  <table className="table border">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Sale type</th>
                        <th>Customer</th>
                        <th>Product</th>
                        <th>Qty (L)</th>
                        <th>Full barrel qty</th>
                        <th>Loose barrel qty</th>
                        <th>Machine detail</th>
                        <th>Selling price</th>
                        <th>Sales value</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(report.rows || []).map((row, index) => (
                        <tr key={`row-${index}`}>
                          <td>{formatReportDate(row.ac_date)}</td>
                          <td>{row.sale_type}</td>
                          <td>{row.customer_name}</td>
                          <td>{row.product_name}</td>
                          <td className="report-amount">{row.quantity}</td>
                          <td className="report-amount">{row.full_barrel_qty}</td>
                          <td className="report-amount">{row.loose_barrel_qty}</td>
                          <td style={{ whiteSpace: 'pre-line' }}>{row.machine_detail}</td>
                          <td className="report-amount">{row.selling_price !== '' ? reportNumberFormat(row.selling_price) : ''}</td>
                          <td className="report-amount">{reportNumberFormat(row.sales_value)}</td>
                          <td>{row.note}</td>
                        </tr>
                      ))}
                      {(report.rows || []).length > 0 && (
                        <tr className="fw-bold">
                          <td colSpan={4}>{totalsRow.label || 'Total'}</td>
                          <td className="report-amount">{totalsRow.quantity}</td>
                          <td colSpan={3} />
                          <td className="report-amount">{reportNumberFormat(totalsRow.selling_price)}</td>
                          <td className="report-amount">{reportNumberFormat(totalsRow.sales_value)}</td>
                          <td />
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </OtherReportResultsZone>
          </OtherReportShell>
        </div>
      </div>
    </div>
  );
};

export default LooseOilBarrelSalesReport;
